import { TASK_STATUS_LABELS, type TaskStatus } from '@netcon-work/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { trpc } from '../../lib/trpc'
import { AufgabenVerweis } from '../AufgabeKontext'

/**
 * **Vorbereitung eines Termins in My Work** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md)).
 *
 * Nils, 23.10.2026: *„Kann ich auch in Punkt 3 klicken und bspw. vorbereiten?"* – Tipp auf einen
 * Termin (im Tag, in „Als Nächstes", im Feierabend) öffnet dieses Blatt. Erst die Vorbereitung vom
 * Vorabend (`notiz.vorbereitungVorabend`, kostet nichts); gibt es keine, „✦ Jetzt vorbereiten" –
 * derselbe Weg wie im Notizbuch (`notiz.vorbereiten`, KI). Bearbeitet und ins Blatt übernommen wird
 * im Notizbuch.
 */

export interface VorzubereitenderTermin {
  titel: string
  beginn: string
}

/** Der Server wirft bei „keine Reihe" einen Fehler – was zurückkommt, ist immer eine Vorbereitung. */
type Vorbereitung = Awaited<ReturnType<typeof trpc.notizbuch.notiz.vorbereiten.mutate>>

const meineQuery = {
  queryKey: ['notizbuch', 'meine'] as const,
  queryFn: () => trpc.notizbuch.meine.query(),
  staleTime: 5 * 60_000,
}

export function VorbereitungBlatt({
  termin,
  schliessen,
}: {
  termin: VorzubereitenderTermin
  schliessen: () => void
}) {
  const { data: meine = [], isLoading: buecherLaden } = useQuery(meineQuery)
  const ids = meine.map((n) => n.id)
  const vorabend = useQuery({
    queryKey: ['mywork', 'vorabend', termin.titel, ids.join('|')],
    queryFn: () =>
      trpc.notizbuch.notiz.vorbereitungVorabend.query({ notizbuchIds: ids, titel: termin.titel }),
    enabled: ids.length > 0 && termin.titel.trim().length >= 3,
    staleTime: 60_000,
  })
  const jetzt = useMutation({
    mutationFn: () =>
      trpc.notizbuch.notiz.vorbereiten.mutate({ notizbuchIds: ids, titel: termin.titel }),
  })

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') schliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [schliessen])

  const fertig: Vorbereitung | null = jetzt.data ?? vorabend.data ?? null
  const fehler = jetzt.error?.message
  const laedt = buecherLaden || vorabend.isLoading
  const wann = new Date(termin.beginn.slice(0, 19)).toLocaleString('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[48] flex items-end justify-center bg-[rgb(23_22_20/0.32)] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Vorbereitung: ${termin.titel}`}
      onClick={schliessen}
    >
      <div
        className="flex max-h-[min(88vh,100%)] w-full max-w-[620px] flex-col gap-3 overflow-y-auto rounded-t-[22px] border border-line bg-surface p-5 pb-[calc(20px+env(safe-area-inset-bottom))] shadow-lg sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12.5px] text-ink2">Vorbereitung · {wann}</div>
            <h2 className="text-[18px] leading-snug font-bold">{termin.titel}</h2>
          </div>
          <button
            type="button"
            onClick={schliessen}
            className="min-h-11 shrink-0 cursor-pointer px-2 text-[14px] font-semibold text-accent-deep"
          >
            Fertig
          </button>
        </div>

        {laedt && <p className="text-[13.5px] text-muted">Wird geladen …</p>}
        {!laedt && meine.length === 0 && (
          <p className="text-[13.5px] text-ink2">Ohne Notizbuch gibt es keine Vorbereitung.</p>
        )}

        {!laedt && meine.length > 0 && !fertig && (
          <div className="flex flex-col gap-2 rounded-xl bg-[#faf9f6] px-3.5 py-3 text-[13.5px]">
            <p>
              Für diesen Termin liegt noch keine Vorbereitung vor. Work sucht die letzten Runden
              derselben Reihe und fasst zusammen, was offen ist.
            </p>
            {fehler && <p className="text-bad">{fehler}</p>}
            <button
              type="button"
              onClick={() => jetzt.mutate()}
              disabled={jetzt.isPending}
              className="min-h-11 cursor-pointer self-start rounded-xl bg-accent px-4 text-[14px] font-semibold text-white disabled:opacity-60"
            >
              {jetzt.isPending ? '✦ wird vorbereitet …' : '✦ Jetzt vorbereiten'}
            </button>
          </div>
        )}

        {fertig && (
          <>
            <div className="text-[12.5px] text-ink2">
              {fertig.runden} {fertig.runden === 1 ? 'Runde' : 'Runden'} · zuletzt{' '}
              <a
                href={`/notizbuch#notiz-${fertig.letzte.nb}-${fertig.letzte.id}`}
                className="font-semibold text-accent-deep"
              >
                {new Date(fertig.letzte.datum).toLocaleDateString('de-DE', {
                  day: 'numeric',
                  month: 'numeric',
                })}
              </a>
              {' · '}
              {fertig.aufgaben.offen} offen, {fertig.aufgaben.erledigt} erledigt
              {fertig.aufgaben.ueberfaellig ? `, ${fertig.aufgaben.ueberfaellig} überfällig` : ''}
            </div>
            {fertig.letztesMal && (
              <Abschnitt titel="Letztes Mal">
                <p className="text-[14px]">{fertig.letztesMal}</p>
              </Abschnitt>
            )}
            {fertig.offen.length > 0 && (
              <Abschnitt titel={`Offen geblieben · ${fertig.offen.length}`}>
                <ul className="flex flex-col gap-1.5">
                  {fertig.offen.map((o, i) => (
                    <li
                      key={`${o.text}-${i}`}
                      className="flex flex-wrap items-center gap-x-2 text-[14px]"
                    >
                      <span className="min-w-0 flex-[1_1_200px]">
                        {o.aufgabe ? (
                          <AufgabenVerweis
                            aufgabeId={o.aufgabe.id}
                            className="hover:text-accent-deep"
                          >
                            {o.text}
                          </AufgabenVerweis>
                        ) : (
                          o.text
                        )}
                        {o.art === 'frage' && <span className="text-muted"> · Frage</span>}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-px text-[11.5px]',
                          !o.aufgabe
                            ? 'bg-[#f3f2ef] text-muted'
                            : o.aufgabe.status === 'erledigt'
                              ? 'bg-good-bg text-good-text'
                              : 'bg-accent-soft text-accent-deep',
                        )}
                      >
                        {o.aufgabe
                          ? (TASK_STATUS_LABELS[o.aufgabe.status as TaskStatus] ??
                            o.aufgabe.status ??
                            '–')
                          : 'keine Aufgabe'}
                      </span>
                    </li>
                  ))}
                </ul>
              </Abschnitt>
            )}
            {fertig.aenderung && (
              <Abschnitt titel="Seitdem">
                <p className="text-[14px]">{fertig.aenderung}</p>
              </Abschnitt>
            )}
            {fertig.tagesordnung.length > 0 && (
              <Abschnitt titel="Tagesordnung">
                <ol className="flex list-decimal flex-col gap-1 pl-5 text-[14px]">
                  {fertig.tagesordnung.map((t, i) => (
                    <li key={`${t.punkt}-${i}`}>
                      {t.punkt}
                      {t.neu && (
                        <span className="ml-1 text-[11.5px] font-semibold text-accent-deep">
                          neu
                        </span>
                      )}
                      {t.notiz && <span className="block text-[12.5px] text-ink2">{t.notiz}</span>}
                    </li>
                  ))}
                </ol>
              </Abschnitt>
            )}
            <a
              href={`/notizbuch#notiz-${fertig.letzte.nb}-${fertig.letzte.id}`}
              className="min-h-11 self-start rounded-xl border border-line2 px-4 py-2.5 text-[14px] font-semibold"
            >
              Letzte Runde im Notizbuch öffnen
            </a>
          </>
        )}
      </div>
    </div>
  )
}

function Abschnitt({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">{titel}</h3>
      {children}
    </section>
  )
}
